import EOrderingLogo from "@assets/images/eOrdering.png";
import SCGPLogo from "@assets/images/SCGP-color.svg";
import { Box, Button, Grid, Menu, MenuItem } from "@material-ui/core";
import { useUser } from "@saleor/auth";
import useMenuStructure from "@saleor/components/AppLayout/menuStructure";
import ChipNumber from "@saleor/components/ChipNumber";
import MyTypography from "@saleor/components/MyTypography";
import { createOrderUrl } from "@saleor/createOrder/urls";
import { useCustomerBlockContext } from "@saleor/customerBlock/hook/useCustomerBlockContext";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import { poUploadUrl } from "@saleor/poUpload/urls";
import classNames from "classnames";
import React, { memo, useRef } from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import useStyles from "./styles";

const HomePageComponent = () => {
  const intl = useIntl();
  const { user } = useUser();
  const classes = useStyles();
  const [menuStructure] = useMenuStructure(intl, user);

  const navigate = useNavigator();
  return (
    <>
      <Box className={classes.root}>
        <Box className={classes.mainPanel}>
          <Box className={classes.headTextLogo}>
            <Box className={classes.text}>
              <img height={16.43} src={SCGPLogo} alt="SCGPlogo" />
            </Box>
            <Box className={classes.logo}>
              <img height={46} src={EOrderingLogo} alt={"logo"} />
            </Box>
          </Box>
          <Box></Box>
        </Box>
        <Box mb={3}>
          <MyTypography variant="body1">
            {intl.formatMessage({
              defaultMessage: "หน้าหลัก"
            })}
          </MyTypography>
        </Box>
        <Grid container spacing={5}>
          {menuStructure.map(
            (menuItem, index) =>
              menuItem.homepageLabel && (
                <SubItem
                  key={menuItem.id}
                  index={index}
                  menuItem={menuItem}
                  menuItemId={menuItem.id}
                  navigate={navigate}
                  classes={classes}
                />
              )
          )}
        </Grid>
      </Box>
    </>
  );
};

interface SubmenuProps {
  index: number | string;
  menuItem;
  menuItemId: string;
  classes;
  navigate: any;
  isChildren?: boolean;
}

const SubItem = (props: SubmenuProps): JSX.Element => {
  const {
    customerBlockStatus,
    onOpenCustomerBlockDialog
  } = useCustomerBlockContext();

  const [_, dispatchAppState] = useAppState();

  const handleResetOrderStateData = () => {
    dispatchAppState({
      payload: {
        filterOrderConfirm: {}
      },
      type: "setFilterOrderConfirm"
    });
    dispatchAppState({
      payload: {
        filterListSalesOrder: {}
      },
      type: "setFilterListSalesOrder"
    });
  };

  const { index, menuItem, menuItemId, classes, navigate, isChildren } = props;
  const refElement = useRef();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickMenuItem = items => {
    if (items.url === createOrderUrl || items.url === poUploadUrl) {
      // Check customer block!
      if (customerBlockStatus) {
        onOpenCustomerBlockDialog();
      } else {
        if (items.children && items.children.length) {
          setAnchorEl(refElement.current);
        } else {
          if (!items?.isRedirectLink) {
            navigate(items.url, { resetScroll: true });
            handleResetOrderStateData();
            handleClose();
            return;
          }
          window.open(items.url);
        }
      }
    } else {
      if (items.children && items.children.length) {
        setAnchorEl(refElement.current);
      } else {
        if (!items?.isRedirectLink) {
          handleResetOrderStateData();
          navigate(items.url, { resetScroll: true });
          handleClose();
          return;
        }
        window.open(items.url);
      }
    }
  };

  return (
    <>
      <Grid
        item
        xs={4}
        key={index}
        onClick={() => handleClickMenuItem(menuItem)}
        className={classNames(classes.menuItem, `item-${index}`)}
        ref={refElement}
        style={{
          marginBottom: isChildren && "0px"
        }}
      >
        {isChildren ? (
          <MyTypography variant="body2">
            {menuItem.homepageLabel || menuItem.label}
          </MyTypography>
        ) : (
          <Button
            variant="contained"
            key={`btn-${index}`}
            color="secondary"
            id={menuItemId}
            data-testid={menuItemId}
            className={classes.itemButton}
          >
            <SVG src={menuItem.iconSrc} />
            <MyTypography
              variant="h7"
              className={classes.menuLabel}
              style={{ textAlign: "left" }}
            >
              {menuItem.homepageLabel}
              <strong style={{ display: "block", fontSize: "1.5rem" }}>
                {menuItem.subText}
              </strong>
            </MyTypography>
            {menuItem?.notifyChip ? (
              <ChipNumber
                number={menuItem?.notifyChip}
                classesChip={classes.chipClass}
              />
            ) : (
              ""
            )}
          </Button>
        )}
      </Grid>
      {menuItem?.children?.length
        ? renderMenuItem(
            anchorEl,
            handleClose,
            classes,
            menuItem,
            menuItemId,
            handleClickMenuItem,
            navigate
          )
        : ""}
    </>
  );
};

function renderMenuItem(
  anchorEl: HTMLElement,
  handleClose: () => void,
  classes: any,
  menuItem: any,
  menuItemId: string,
  handleClickMenuItem: (items: any) => void,
  navigate: any
) {
  return (
    <Menu
      anchorEl={anchorEl}
      keepMounted
      open={!!anchorEl}
      elevation={0}
      disableAutoFocusItem
      onClose={handleClose}
      anchorOrigin={{ vertical: "top", horizontal: 170 }}
      transitionDuration={500}
      PaperProps={{
        className: classes.paperMenu
      }}
      MenuListProps={{
        className: classes.parentMenu,
        "aria-labelledby": "basic-button"
      }}
      data-testid="dropdown-submenu"
    >
      {menuItem.children.map((children, childrenIndex) => (
        <MenuItem
          key={children.id}
          id={`${menuItemId}-${children.id}`}
          data-testid={`${menuItemId}-${children.id}`}
          onClick={() => handleClickMenuItem(children)}
          className={classes.childrenMenu}
        >
          {children.children ? (
            <SubItem
              isChildren={true}
              menuItem={children}
              menuItemId={`${menuItemId}-${children.id}`}
              classes={classes}
              navigate={navigate}
              index={`${children.id}-${childrenIndex}`}
            />
          ) : (
            <MyTypography variant="body2">
              {children?.homepageLabel || children?.label}
            </MyTypography>
          )}
        </MenuItem>
      ))}
    </Menu>
  );
}

export default memo(HomePageComponent);
